/**
 * Lot Focus Plugin for Native Interactive Site Map (ISM)
 *
 * Sole owner of the map's camera (zoom + center) reacting to lot selection: zooms to
 * LOT_FOCUS_ZOOM and centers the selected lot within the visible map viewport, for every lot
 * selection (including multi-plan lots that open the lotMapSelection picker). Deliberately
 * separate from `lotInteraction.plugin.js` (owns coloring/click handling only, not camera — see
 * LOT_INTERACTION_PLUGIN_SEQUENCE.md's ownership table) and from `drawer.plugin.js` (owns the
 * mobile bottom sheet's own expand/collapse, not the map).
 *
 * @module nativeInteractiveSiteMap/plugins/lotFocus.plugin
 */
import fastdom from 'fastdom';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { selectSelectedLotId } from '../redux/selectors';
import { LOT_FOCUS_ZOOM } from '../constants';
import createLotFocusHandlers from '../handlers/lotFocusHandlers';

export default function createLotFocusPlugin() {
  const state = {
    overlay: null,
    svgElement: null,
    svgOverlaySubscription: null,
    selectedLotSubscription: null,
    focusRequestId: 0,
    handlers: createLotFocusHandlers(),
  };

  function focusOnLot(context, lotId) {
    if (!state.overlay || !state.svgElement) {
      return;
    }

    const { lotElement, mapElement } = state.handlers.getFocusElements(state.svgElement, context.root, lotId);
    if (!lotElement || !mapElement) {
      return;
    }

    state.focusRequestId += 1;
    const focusRequestId = state.focusRequestId;
    // Geometry reads now run in fastdom.measure(), while map zoom/center/pan updates 
    // run in fastdom.mutate(). A request ID prevents stale queued callbacks from 
    // applying after another lot is selected or the plugin stops.
    fastdom.measure(() => {
      const divPixel = state.handlers.getLotOffsetFromMapCenter(lotElement, mapElement);
      const latLng = state.overlay.projectDivPixelToLatLng(divPixel);
      const reservedHeight = context.state.isMobile ? state.handlers.getMobileDrawerReservedHeight(mapElement) : 0;

      fastdom.mutate(() => {
        if (focusRequestId !== state.focusRequestId || !latLng) {
          return;
        }

        const currentZoom = context.services.mapFacade.getCameraSnapshot().zoom;
        context.services.mapFacade.setMapZoom(currentZoom > 18 ? currentZoom : LOT_FOCUS_ZOOM);
        context.services.mapFacade.updateMapCenter(latLng);

        if (reservedHeight > 0) {
          // Shift the camera upward so the selected lot is centered
          // in the visible area above the drawer.
          context.services.mapFacade.panBy(0, reservedHeight / 2);
        }
      });
    });
  }

  return {
    name: 'lot-focus',

    async start(context) {
      function onSelectedLotIdChanged(lotId) {
        focusOnLot(context, lotId);
      }

      state.svgOverlaySubscription = context.services.svgOverlayReady$.subscribe(({ overlay, svgElement }) => {
        state.overlay = overlay;
        state.svgElement = svgElement;
      });

      state.selectedLotSubscription = context.services.store$.pipe(
        map(selectSelectedLotId),
        distinctUntilChanged(),
        filter(Boolean),
      ).subscribe(onSelectedLotIdChanged);
    },

    async stop() {
      state.svgOverlaySubscription?.unsubscribe?.();
      state.svgOverlaySubscription = null;

      state.selectedLotSubscription?.unsubscribe?.();
      state.selectedLotSubscription = null;

      state.focusRequestId += 1;
      state.overlay = null;
      state.svgElement = null;
    },
  };
}
